// src/components/CustomFields/fields/ComboBoxField.tsx
// ComboBoxField component migrated from legacy

import React from 'react';
import CustomFieldsService from '../../../api/CustomFieldsService';
import $ from 'jquery';
import Select from 'react-select';
import type { SingleValue, StylesConfig } from 'react-select';
import './ComboBoxField.css';
import { RenderInBody } from '../../Globals';
import shortid from 'shortid';
import { isSafari } from './DOMHelper';
import type { ComboBoxFieldProps, CustomFieldItem } from './types';

// react-select v5 styles matching legacy Corgee appearance
const getComboBoxSelectStyles = (isDark: boolean): StylesConfig<any, false> => ({
  control: (base, state) => ({
    ...base,
    minHeight: '24px',
    height: '24px',
    borderRadius: 0,
    boxShadow: 'none',
    fontSize: '11px',
    backgroundColor: isDark ? '#2d2f2f' : base.backgroundColor,
    borderColor: state.isFocused ? '#62accb' : isDark ? '#4a4c4c' : '#ccc',
    width: '125px',
    '&:hover': {
      borderColor: '#62accb',
    },
  }),
  valueContainer: base => ({
    ...base,
    height: '24px',
    padding: '0 6px',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    height: '22px',
    color: isDark ? '#a9a093' : base.color,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: () => ({
    display: 'none',
  }),
  clearIndicator: base => ({
    ...base,
    padding: '2px',
    width: '20px',
    color: isDark ? '#6e6860' : base.color,
    '&:hover': {
      color: isDark ? '#a9a093' : '#666',
    },
  }),
  placeholder: base => ({
    ...base,
    lineHeight: '22px',
    fontSize: '11px',
    color: isDark ? '#6e6860' : '#aaa',
  }),
  singleValue: base => ({
    ...base,
    lineHeight: '22px',
    fontSize: '11px',
    color: isDark ? '#a9a093' : '#333',
  }),
  menu: base => ({
    ...base,
    marginTop: 0,
    borderRadius: 0,
    width: '125px',
    zIndex: 9999,
    backgroundColor: isDark ? '#2d2f2f' : '#fff',
    border: isDark ? '1px solid #4a4c4c' : base.border,
    boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.5)' : base.boxShadow,
  }),
  menuList: base => ({
    ...base,
    padding: 0,
    maxHeight: '130px',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '11px',
    lineHeight: '1.8',
    padding: '2px 6px',
    minHeight: 'auto',
    backgroundColor: state.isFocused ? '#62accb' : isDark ? '#2d2f2f' : '#fff',
    color: state.isFocused ? '#fff' : isDark ? '#a9a093' : '#666666',
    border: state.isFocused ? '1px solid #eee' : '1px solid transparent',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#62accb',
    },
  }),
  noOptionsMessage: base => ({
    ...base,
    fontSize: '11px',
    color: isDark ? '#6e6860' : base.color,
    backgroundColor: isDark ? '#2d2f2f' : base.backgroundColor,
  }),
});

interface SelectedValue {
  label: string;
  value: number | string;
}

interface ComboBoxFieldState {
  selectedValue: SelectedValue;
  EditorEnabled: boolean;
  editableValue: string;
  previousWidth: number;
  disableAutoSave: boolean;
  customField: CustomFieldItem;
  canEdit: boolean;
  apiConfig: { url: string; token: string };
  previousValue: string;
  listMenuStyle: { width: number };
  scrollTop: number;
  elementId: string;
  isProjectView: boolean;
  isFromInfoWindow: boolean;
  isFromTabDetails: boolean;
  isConversationDetailsView: boolean;
  listPosition: { left: number; top: number };
  options: any[];
}

class ComboBoxField extends React.Component<ComboBoxFieldProps, ComboBoxFieldState>
{
  private comboBoxRef = React.createRef<HTMLDivElement>();
  private selectComboBoxRef = React.createRef<any>();
  private _blurHandled = false;

  constructor(props: ComboBoxFieldProps)
  {
    super(props);
    const cf = props.customField;

    this.state = {
      selectedValue: {
        label: cf.SelectedValuesText !== '' ? cf.SelectedValuesText || '' : '',
        value: cf.Value != null ? parseInt(String(cf.Value)) : '',
      },
      EditorEnabled: props.EditorEnabled || false,
      editableValue: '',
      previousWidth: 0,
      disableAutoSave: props.disableAutoSave || false,
      customField: cf,
      canEdit: props.canEdit && cf.ConversationIsEnable !== false,
      apiConfig: props.apiConfig,
      previousValue: '',
      listMenuStyle: {
        width: 150,
      },
      scrollTop: 0,
      elementId: shortid.generate(),
      isProjectView: window.location.href.indexOf('ProjectDetails') > -1,
      isFromInfoWindow: false,
      isFromTabDetails: false,
      isConversationDetailsView: window.location.href.indexOf('ConversationDetails') > -1,
      listPosition: {
        left: 0,
        top: 0,
      },
      options: [],
    };

    this.handleChange = this.handleChange.bind(this);
    this.save = this.save.bind(this);
    this.enableEditor = this.enableEditor.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onUpdateCustomField = this.onUpdateCustomField.bind(this);
    this.loadData = this.loadData.bind(this);
    this.onApplyStyle = this.onApplyStyle.bind(this);
    this.onCloseSelect = this.onCloseSelect.bind(this);
    this.onKeyDownSelect = this.onKeyDownSelect.bind(this);
  }

  componentDidMount()
  {
    if (this.comboBoxRef.current)
    {
      const containerElement = $(this.comboBoxRef.current);
      if (containerElement.closest('.details-tab-directive').length > 0)
      {
        this.setState({ isFromInfoWindow: true });
      }
      if (containerElement.closest('#canvasDetails').length > 0)
      {
        this.setState({ isFromTabDetails: true });
      }
    }
  }

  onApplyStyle()
  {
    if (!this.state.isFromInfoWindow && !this.state.isFromTabDetails)
    {
      const isProjectView = window.location.href.indexOf('ProjectDetails') > -1;
      this.setState({ isProjectView: isProjectView });

      const containerElement = $(this.comboBoxRef.current!);
      const ns = `scroll.combobox-${this.state.elementId}`;
      containerElement
        .closest('.layer-custom-fields-container')
        .first()
        .off(ns)
        .on(ns, this.onCloseSelect);
    }
  }

  onCloseSelect()
  {
    this.setState({ EditorEnabled: false });
  }

  onKeyDownSelect(e: React.KeyboardEvent): void
  {
    if ((e.key === 'Delete' || e.key === 'Backspace') && this.state.selectedValue.value !== '')
    {
      e.preventDefault();
      this.setState({ selectedValue: { value: '', label: '' } });
    }
  }

  componentWillReceiveProps(nextProps: ComboBoxFieldProps)
  {
    // Only update if the value actually changed to prevent infinite loops
    const currentValue = String(this.state.customField.Value || '');
    const nextValue = String(nextProps.customField.Value || '');

    if (
      currentValue !== nextValue ||
      this.state.customField.CustomFieldId !== nextProps.customField.CustomFieldId
    )
    {
      const data = {
        value:
          nextProps.customField.Value != null ? parseInt(String(nextProps.customField.Value)) : '',
        label:
          nextProps.customField.SelectedValuesText !== ''
            ? nextProps.customField.SelectedValuesText || ''
            : '',
      };
      this.setState({
        selectedValue: data,
        customField: nextProps.customField,
      });
    }
  }

  handleChange(event: SingleValue<any>)
  {
    if (event != null)
    {
      const valueField = (this.state.customField as any).ValueField;
      const textField = (this.state.customField as any).TextField;
      const data = {
        value: event[valueField] ?? event.value,
        label: event[textField] ?? event.label,
      };
      this.setState({ selectedValue: data }, () =>
      {
        // Close dropdown and save immediately after selecting an option
        setTimeout(() => this.onBlur(), 0);
      });
    }
    else
    {
      const blank = { value: '', label: '' };
      this.setState({ selectedValue: blank });
    }
  }

  save()
  {
    const { selectedValue, editableValue, disableAutoSave } = this.state;

    const copyCustomField = { ...this.state.customField };

    // We make sure that the new value is different to the previous one
    if (selectedValue.value !== editableValue && selectedValue.value != null)
    {
      // Assign the new values
      copyCustomField.Value = selectedValue.value;
      (copyCustomField as any).SelectedValuesText = selectedValue.label;
      copyCustomField.PreviousValue = editableValue;
      this.setState({ editableValue: String(selectedValue.value) });

      // Do not save if autoSaved is disabled
      if (!disableAutoSave)
      {
        const requestSave = {
          api: this.props.apiConfig,
          request: copyCustomField,
        };

        if (
          copyCustomField.CustomFieldValueId === undefined ||
          copyCustomField.CustomFieldValueId === null ||
          copyCustomField.CustomFieldValueId === 0
        )
        {
          CustomFieldsService.savePost(requestSave).then((data: any) =>
          {
            copyCustomField.CustomFieldValueId = data.CustomFieldValueId;

            try
            {
              this.setState({ customField: copyCustomField }, () =>
              {
                this.onUpdateCustomField();
              });
            }
            catch (err)
            {
              // Handle error
            }
          });
        }
        else
        {
          CustomFieldsService.save(requestSave).then(() =>
          {
            try
            {
              this.setState({ customField: copyCustomField }, () =>
              {
                this.onUpdateCustomField();
              });
            }
            catch (err)
            {
              // Handle error
            }
          });
        }
      }
      else
      {
        this.setState({ customField: copyCustomField }, () =>
        {
          this.onUpdateCustomField();
        });
      }
    }
    this.setState({ EditorEnabled: false });
  }

  enableEditor(e: React.MouseEvent)
  {
    const { canEdit, selectedValue } = this.state;

    if (canEdit)
    {
      this._blurHandled = false;
      const screenWidth = $('body');
      const widthScreen = screenWidth[0]?.offsetWidth ?? 0;
      const windowX = e.clientX;
      const elemCustField = (e.target as HTMLElement).closest(
        'div.corgee_com_custom-fields-field-A',
      ) as HTMLElement;

      this.setState(
        {
          previousWidth: elemCustField?.offsetWidth || 0,
        },
        () =>
        {
          if (155 + windowX > widthScreen && elemCustField)
          {
            elemCustField.className += ' responsiveInfoDetail ';
            elemCustField.style.width = '135px';
          }
        },
      );

      setTimeout(() =>
      {
        this.loadData();
        if (this.selectComboBoxRef.current)
        {
          this.selectComboBoxRef.current.focus();
        }
      });

      if (!isSafari())
      {
        $('body').css('overflow', 'hidden');
      }
      const window$ = $('.corgee_com_custom-fields-combobox-multiselect');
      const element = $(this.comboBoxRef.current!);
      if (window$.length > 0)
      {
        element.closest('.layer-details-container').css('overflow', 'hidden');
      }

      const infoWindowScrollContainer = element.closest('.details-tab-directive .card-body-area');
      if (infoWindowScrollContainer.length > 0)
      {
        infoWindowScrollContainer.css('overflow', '');
      }

      this.setState({ EditorEnabled: true, editableValue: String(selectedValue.value) }, () =>
      {
        const windowModal = $('.corgee_com_custom-fields-combobox-multiselect');
        const element$ = $(this.comboBoxRef.current!);
        const control = element$.find('#select-target-' + this.state.elementId);
        let top = 0;
        let left = 0;

        if (
          windowModal.offset() &&
          windowModal.length > 0 &&
          windowModal.find('[id*="select-render"]').length > 0
        )
        {
          left = control.offset()!.left - windowModal.offset()!.left;
          top = control.offset()!.top + 35 - windowModal.offset()!.top;
        }
        else
        {
          if (control.length > 0)
          {
            left = control.offset()!.left;
            top = control.offset()!.top - ($('html').scrollTop() || 0);

            if (isSafari())
            {
              top = top - ($('body').scrollTop() || 0);
            }
          }
        }

        this.setState({
          listPosition: {
            left: left,
            top: top,
          },
        });

        const scrollNs = `scroll.combobox-${this.state.elementId}`;
        setTimeout(() =>
        {
          element$
            .closest('.layer-custom-fields-container')
            .off(scrollNs)
            .on(scrollNs, () =>
            {
              this.onBlur();
            });

          element$
            .closest('.corgee_com_card-body-info')
            .off(scrollNs)
            .on(scrollNs, () =>
            {
              this.onBlur();
            });
        }, 100);
      });

      const clickNs = `click.combobox-${this.state.elementId}`;
      setTimeout(() =>
      {
        $(document)
          .off(clickNs)
          .on(clickNs, e =>
          {
            const selectorOption = $('.Select-option');
            const target = $(e.target);
            if ((target as any) !== (selectorOption as any))
            {
              this.onBlur();
            }
          });
      }, 100);
    }
  }

  onBlur()
  {
    if (this._blurHandled) return;
    this._blurHandled = true;

    const { selectedValue, previousValue } = this.state;

    $('body').css('overflow', '');
    const elemReset = document.getElementsByClassName('responsiveInfoDetail');
    if (elemReset.length !== 0)
    {
      (elemReset[0] as HTMLElement).style.width = `${this.state.previousWidth}px`;
      (elemReset[0] as HTMLElement).className =
        'corgee_com_custom-fields-field-A _DEL_field-A custom-field-id';
    }

    const element = $(this.comboBoxRef.current!);
    const ns = `scroll.combobox-${this.state.elementId}`;
    const clickNs = `click.combobox-${this.state.elementId}`;
    element.closest('.layer-details-container').css('overflow', '');
    element.closest('.layer-details-container').off(ns);
    element.closest('.card-body-area').off(ns);
    element.closest('md-dialog-content').off(ns);
    element.closest('.add-inline-task').off(ns);
    element.closest('.corgee_com_card-body-info').off(ns);
    element.closest('.layer-custom-fields-container').off(ns);
    $(document).off(clickNs);

    // Get new values
    if (String(selectedValue.value).length === 0)
    {
      this.setState({ previousValue: String(selectedValue.value) });
      this.save();
      return;
    }

    if (previousValue === String(selectedValue.value) || !selectedValue.label)
    {
      this.onCloseSelect();
      return;
    }

    // Save changes
    this.save();
  }

  loadData()
  {
    const { customField } = this.state;
    if (customField)
    {
      const requestSave = {
        api: this.props.apiConfig,
        serviceUrl: (customField as any).ServiceUrl,
      };

      CustomFieldsService.getListComboBox(requestSave).then((data: any) =>
      {
        this.setState({ options: data });
      });
    }
  }

  onUpdateCustomField()
  {
    if (this.props.UpdateCustomFields)
    {
      this.props.UpdateCustomFields(this.state.customField);
    }
  }

  render()
  {
    const {
      EditorEnabled,
      canEdit,
      selectedValue,
      options,
      customField,
      isFromInfoWindow,
      isFromTabDetails,
      listPosition,
    } = this.state;
    const isDark = document.documentElement.classList.contains('dark');
    const comboBoxSelectStyles = getComboBoxSelectStyles(isDark);
    const style: React.CSSProperties = {
      position: 'fixed',
      zIndex: 999999,
      top: listPosition.top,
      left: listPosition.left,
    };
    const containerSelectorId = 'select-target-' + this.state.elementId;

    if (isFromInfoWindow || isFromTabDetails)
    {
      (style as any).fontSize = 15;
      style.position = 'absolute';
    }

    return (
      <div
        ref={this.comboBoxRef}
        className="corgee_com_custom-fields-field-A _DEL_field-A custom-field-id"
      >
        {customField.GroupMultiEditionType === 1 && (
          <i
            className="material-icons several-values enable-help-tooltip"
            title="Not all cards selected have this detail. The detail value will change only on applicable cards."
            data-title="Not all cards selected have this detail. The detail value will change only on applicable cards."
          >
            do_not_disturb_on
          </i>
        )}
        <span className="corgee_com_custom-fields-title _DEL_title">{customField.Name}</span>
        <div className="click-to-edit">
          {!EditorEnabled ? (
            <div
              onClick={this.enableEditor}
              className={canEdit ? 'lbl-editable-input' : 'lbl-non-editable-input'}
            >
              {selectedValue.label !== undefined && selectedValue.label !== ''
                ? selectedValue.label
                : 'Not Set'}
            </div>
          ) : null}
          {EditorEnabled && (
            <div id={containerSelectorId} style={{ margin: 0, padding: 0 }}>
              <RenderInBody
                containerStyles={
                  "position:'fixed'; top:" +
                  listPosition.top +
                  'px; left:' +
                  listPosition.left +
                  'px; zIndex:999999'
                }
              >
                <div
                  id={'select-render-' + this.state.elementId}
                  className="corgee_com_custom-fields-combobox _DEL_comboBoxRef"
                  style={style}
                >
                  <Select
                    name="form-field-name"
                    ref={this.selectComboBoxRef}
                    autoFocus={true}
                    menuIsOpen={true}
                    options={options}
                    onChange={this.handleChange}
                    onMenuOpen={this.onApplyStyle}
                    onKeyDown={this.onKeyDownSelect}
                    value={
                      options.find(
                        (opt: any) =>
                          opt[(customField as any).ValueField] === selectedValue.value ||
                          String(opt[(customField as any).ValueField]) ===
                            String(selectedValue.value),
                      ) || null
                    }
                    isSearchable={true}
                    closeMenuOnSelect={true}
                    isClearable={true}
                    getOptionLabel={(opt: any) => opt[(customField as any).TextField]}
                    getOptionValue={(opt: any) => String(opt[(customField as any).ValueField])}
                    styles={comboBoxSelectStyles}
                  />
                </div>
              </RenderInBody>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ComboBoxField;
